// The View options trigger and panel: the toolbar's final control, a sliders icon that opens a non-modal
// dialog. Density comes first, then the column list in current order, then Reset column widths after a
// divider while any width differs from its configured initial value. Every choice commits at once and
// publishes the complete View with reason 'view'.

import React, { useCallback, useRef } from 'react'

import { Button, Menu, SegmentedControl } from '@CommonComponents/buttons'
import { useTable } from '../context'
import { ACTIONS } from '../types'
import { ColumnList } from './ColumnList'
import { VIEW_COPY } from './copy'
import { useDensityAttribute } from './useDensity'
import { reconcileView, widthsDiffer } from './view'

const DENSITY_OPTIONS = [
  { value: 'comfortable', label: VIEW_COPY.comfortable },
  { value: 'compact', label: VIEW_COPY.compact },
]

const announce = message => {
  const runtime = typeof window !== 'undefined' ? window.MEButtons : null
  if (runtime && typeof runtime.announce === 'function')
    runtime.announce(message)
}

export const ViewOptions = () => {
  const { config, state, dispatch, geometry, refs } = useTable()
  const { columns } = config
  const view = reconcileView(state.view, columns, config.defaultDensity)
  const anchor = useRef(null)
  useDensityAttribute(anchor, refs, view.density)
  const panel = useRef(null)
  const invalidate = geometry && geometry.invalidate

  const commit = useCallback(
    change => {
      dispatch({ type: ACTIONS.viewSet, view: change, reason: 'view' })
      if (typeof invalidate === 'function') invalidate()
    },
    [dispatch, invalidate]
  )

  // the reset control leaves with its own commit, so focus moves to the density control
  const resetWidths = () => {
    const density =
      panel.current &&
      panel.current.querySelector('[role="radio"][tabindex="0"]')
    commit({ widths: {} })
    announce(VIEW_COPY.widthsReset)
    if (density) density.focus()
  }

  return (
    <>
      <span ref={anchor} className="me-table-view-anchor" hidden />
      <Menu
        id={`${config.tableId}-view-options`}
        label={VIEW_COPY.trigger}
        mode="panel"
        iconOnly
        icon="fa-regular fa-sliders"
        align="end"
        className="me-btn-icon me-table-view-trigger"
      >
        <div ref={panel} className="me-table-view">
          <SegmentedControl
            small
            label={VIEW_COPY.density}
            value={view.density}
            options={DENSITY_OPTIONS}
            data-success-label={VIEW_COPY.densityChanged}
            data-failure-label={VIEW_COPY.densityFailed}
            onChange={density => {
              if (density !== view.density) commit({ density })
            }}
          />
          <ColumnList columns={columns} view={view} commit={commit} />
          {widthsDiffer(columns, view) && (
            <div className="me-panel-foot">
              <Button
                className="me-btn me-btn-ghost me-btn-sm"
                onClick={resetWidths}
              >
                {VIEW_COPY.resetWidths}
              </Button>
            </div>
          )}
        </div>
      </Menu>
    </>
  )
}
